import { useEffect, useState } from "react"

import { getFinancialTransactionItem } from "../../Project/api/project-finance.services.ts"
import { FINANCE_ITEM_STATUSES } from "../../Project/ProjectViewPage/constants/finance-item-constants.ts"

export interface InvoicePayment {
  id: string
  value: string
  status?: number
  status_display?: string
  payment_method_display?: string
  payment_date?: string | null
  lineName?: string
}

interface PaymentsState {
  loading: boolean
  /** Completed payments only: the ones `paid_to_date` adds up. */
  payments: InvoicePayment[]
  /** Booked against the invoice but not paid yet (planned, pending, partial). */
  scheduled: InvoicePayment[]
  /** At least one linked line could not be read, so the list may be short. */
  incomplete: boolean
}

interface PaymentSource {
  id: string
  lines?: { finance_line: string; finance_line_name?: string }[]
}

const IDLE: PaymentsState = {
  loading: false,
  payments: [],
  scheduled: [],
  incomplete: false
}

const byPaymentDate = (a: InvoicePayment, b: InvoicePayment) =>
  (a.payment_date ?? "").localeCompare(b.payment_date ?? "")

/**
 * The payments booked against an invoice, read from the lines it bills.
 *
 * The invoice payload carries `paid_to_date` but not the payments behind it;
 * each payment sits on a finance line and names the invoice it settles.
 * The backend counts only completed payments into `paid_to_date`, so only those
 * are listed as paid; cancelled ones are dropped.
 */
export const useInvoicePayments = (
  invoice: PaymentSource | null
): PaymentsState => {
  const [state, setState] = useState<PaymentsState>(IDLE)
  const invoiceId = invoice?.id
  const lineKey = [
    ...new Set((invoice?.lines ?? []).map((line) => line.finance_line))
  ].join(",")

  useEffect(() => {
    const lineIds = lineKey ? lineKey.split(",") : []
    if (!invoiceId || !lineIds.length) {
      setState(IDLE)
      return
    }
    let current = true
    setState({ ...IDLE, loading: true })

    Promise.allSettled(lineIds.map((id) => getFinancialTransactionItem(id)))
      .then((results) => {
        if (!current) return
        const payments: InvoicePayment[] = []
        const scheduled: InvoicePayment[] = []
        let incomplete = false
        results.forEach((result) => {
          if (result.status !== "fulfilled" || !result.value?.ok) {
            incomplete = true
            return
          }
          const line = result.value.data
          ;(line?.partial_finances ?? [])
            .filter((payment) => payment.invoice === invoiceId)
            .forEach((payment) => {
              if (payment.status === FINANCE_ITEM_STATUSES.CANCELLED) return
              const target =
                payment.status === FINANCE_ITEM_STATUSES.COMPLETED
                  ? payments
                  : scheduled
              target.push({ ...payment, lineName: line?.name })
            })
        })
        payments.sort(byPaymentDate)
        scheduled.sort(byPaymentDate)
        setState({ loading: false, payments, scheduled, incomplete })
      })

    return () => {
      current = false
    }
  }, [invoiceId, lineKey])

  return state
}
